/**
 * PaidSelect - Payer/Account selector for product rows
 *
 * Uses centralized styling from styles/utilities/view-agreement.ts
 * for consistent field heights and appearance.
 */

import React, { useState, useEffect } from 'react'
import { Box, Typography, Select, MenuItem, InputLabel, useTheme, FormControl } from '@mui/material'
import GroMarketingIcon from '../Icons/GroMarketingIcon'
import CorporateFareIcon from '@mui/icons-material/CorporateFare'
import { productSelectSx } from 'styles/utilities/view-agreement'

interface PaidSelectProps {
  context?: string
  displayLabel?: string
  value: string
  setterFn: (value: string) => void
  disabled?: boolean
  isDraft?: boolean
}

const PaidSelect: React.FC<PaidSelectProps> = ({
  context,
  displayLabel,
  value,
  setterFn,
  disabled,
  isDraft,
}) => {
  const theme = useTheme()
  const [selectedValue, setSelectedValue] = useState(value)

  useEffect(() => {
    setSelectedValue(value)
  }, [value])

  const handleChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const newValue = event.target.value as string
    setSelectedValue(newValue)
    setterFn(newValue)
  }

  const paidOptions = [
    { label: 'GRO', value: 'GRO' },
    { label: 'Client', value: 'Client' },
  ]

  // Get centralized styling
  const selectStyles = productSelectSx(theme)

  // Determine if field should be disabled
  const isDisabled = !isDraft ? true : isDraft && context === 'payer' ? disabled : disabled

  return (
    <FormControl fullWidth variant="outlined" size="small" sx={selectStyles}>
      <InputLabel id={`${context || 'paidOptions'}-label`} shrink>
        {displayLabel || ''}
      </InputLabel>
      <Select
        labelId={`${context || 'paidOptions'}-label`}
        id={`${context}-select`}
        value={selectedValue || 'GRO'}
        disabled={isDisabled}
        onChange={handleChange as any}
        label={displayLabel}
        renderValue={(selected) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {selected === 'GRO' ? (
              <GroMarketingIcon disabled={disabled} />
            ) : (
              <Typography sx={{ fontSize: 13 }}>Client</Typography>
            )}
          </Box>
        )}
      >
        {paidOptions.map((option) => (
          <MenuItem key={option.value} value={option.value} sx={{ py: 1 }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 2,
                width: '100%',
              }}
            >
              <Typography sx={{ fontSize: 13 }}>{option.label}</Typography>
              {option.value === 'GRO' && (
                <GroMarketingIcon width={36} radius="18px" bgColor="transparent" />
              )}
              {option.value === 'Client' && (
                <CorporateFareIcon sx={{ color: 'rgb(114, 229, 218)', fontSize: 20 }} />
              )}
            </Box>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  )
}

export default PaidSelect
